import {
  DataTable,
  DataTableColDef,
} from "@/lib/Components/DataTable/DataTable";
import {
  TripListItem,
  tripListQuery,
} from "@/app/Cities/Trips/GraphQL/tripList.graphql";
import type { TableId } from "@/app/Common/Utils/table-id";
import { ReactNode } from "react";

import {
  Measurement,
  OrderByRelationWithColumnAggregateFunction,
  QueryTripsOrderCityAColumn,
  SortOrder,
  TripListQueryVariables,
} from "@/gql/graphql";
import { ModelLinkCell } from "@/lib/Components/DataTable/Cells/ModelLinkCell";
import { DeEmphasisedCell } from "@/lib/Components/DataTable/DeEmphasisedCell";
import { formatDistance } from "@/lib/Formatters/formatCurrency";
import { useTripActions } from "@/app/Cities/Trips/Hooks/useTripActions";
import { tripName } from "@/app/Cities/Trips/Utils/tripName";
type TripTableColumnId = "trip" | "distance" | "duration" | "faqs";
export function TripTable({
  id,
  queryVariables,
  filters,
}: {
  id: TableId;
  queryVariables?: Partial<TripListQueryVariables>;
  filters?: ReactNode;
}) {
  const getActions = useTripActions();

  const columns: DataTableColDef<TripListItem, any, TripTableColumnId>[] = [
    {
      id: "trip",
      header: "Trip",
      cell: ({ row }) => (
        <ModelLinkCell
          from="/$tenant/$orgId/supplier/{-$supplierId}"
          to="/$tenant/$orgId/supplier/{-$supplierId}/admin/trips/$tripId"
          params={(prev) => ({ ...prev, tripId: row.original.id })}
          viewTransition
        >
          {tripName(row.original)}
        </ModelLinkCell>
      ),
    },
    {
      id: "distance",
      header: "Distance",
      accessorFn: (data) =>
        data.distance_kms
          ? formatDistance(data.distance_kms, Measurement.Metric)
          : "N/A",
      cell: DeEmphasisedCell,
    },
    {
      id: "duration",
      header: "Duration",
      accessorFn: (data) =>
        data.duration_hrs ? `${data.duration_hrs} hrs` : "N/A",
      cell: DeEmphasisedCell,
    },
    {
      id: "faqs",
      header: "FAQs",
      accessorFn: (data) => data.faqs_count || "Regional",
      cell: DeEmphasisedCell,
    },
  ];

  return (
    <DataTable
      searchable={false}
      filters={filters}
      columns={columns}
      document={tripListQuery}
      getActions={getActions}
      getQueryVariables={({ pagination }) => {
        return {
          first: pagination.pageSize,
          page: pagination.pageIndex,
          order: [
            {
              cityA: {
                column: QueryTripsOrderCityAColumn.Name,
                aggregate: OrderByRelationWithColumnAggregateFunction.Min,
              },
              order: SortOrder.Asc,
            },
          ],
          ...queryVariables,
        };
      }}
      accessor={(data) => data.trips}
      id={id}
    />
  );
}
